import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { TaskTypeChartValueField } from "ui/Scripts/source/models/task/taskTypeChartValueField";
import { TaskTypeChartGroupingField } from "ui/Scripts/source/models/task/taskTypeChartGroupingField";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { TaskDataRowDataType, TaskTypeClassHidden, TaskTypeClassWidthPrefix } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { IAppReportTaskTypeTitlePage } from "ui/Scripts/source/models/appReportTemplate/appReportTaskTypeTitlePage";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Translations } from "ui/Scripts/source/models/translations";

export const defaultExpression = "1 + 1";

export interface ITaskTypeFieldPropUpdate {
    field: string;
    name: string;
    value: string | boolean | number | IIdTitle[] | IAppReportTaskTypeTitlePage;
}

export interface ITaskTypeChartPropUpdate {
    name: string;
    prop: string;
    value: string | boolean | number | TaskTypeChartGroupingField[] | TaskTypeChartValueField[];
}

export interface IFormDesignerDragging {
    width: number;
    source?: string;
}

/* eslint-disable no-unused-vars */
export const enum TaskTypeDialogChartDesignerDroppableId {
    Picker = "chartPickerDroppable",
    Design = "designAreaDroppable"
}
/* eslint-enable no-unused-vars */

export const updateTaskTypeColPropUpdate = (taskTypeCols: ITaskTypeCol[], values: ITaskTypeFieldPropUpdate[]) => {
    let typeCol: ITaskTypeCol = null;
    const result = taskTypeCols.map((item) => {
        const fieldValues = values.filter(i => i.field === item.field);
        if (fieldValues.length) {
            const res = { ...item };
            typeCol = res;
            for (const value of fieldValues) {
                res[value.name] = value.value;
                //idOptions change
                if (value.name === "idOptions") {
                    typeCol.options = [];
                }
                //title change
                if (value.name === "title") {
                    const valueStr = value.value as string;
                    // DO NOT SET PLACEHOLDER AUTOMATICALLY
                    // if (valueStr && typeCol.dataType === TaskDataRowDataType.String) {
                    //     typeCol.placeholder = valueStr;
                    // } else
                    if (typeCol.dataType === TaskDataRowDataType.Label) {
                        //Dynamically set preWrap
                        const hasPreWrap = typeCol.class.includes("preWrap");
                        if (valueStr && valueStr.indexOf("\n") > -1) {
                            if (!hasPreWrap) {
                                typeCol.class = `${typeCol.class} preWrap`;
                            }
                        } else if (hasPreWrap) {
                            typeCol.class = typeCol.class.replace(" preWrap", "").replace("preWrap ", "");
                        }
                    }
                }
            }
            return res;
        }
        return { ...item };
    });
    return {
        taskTypeCols: result,
        typeCol
    };
};

export const updateDefaultValueToDataRowCols = (taskDataRow: ITaskDataRow, typeCol: ITaskTypeCol, id: string) => {
    if (!taskDataRow?.cols) return;
    const ind = taskDataRow.cols.findIndex(i => i.field === id);
    if (ind < 0) return;
    const dataCol = TaskDataRowCol.getDefaultValueTaskDataRowCol(typeCol);
    dataCol.valueStr = TaskDataRowCol.getTaskDataRowColUiValue(typeCol, dataCol.valueStr, dataCol.valueNbr);
    taskDataRow.cols[ind] = dataCol;
};

export const updateValueToDataRowCol = (taskDataRow: ITaskDataRow, field: string, name: string, value: string | number | boolean) => {
    if (!taskDataRow?.cols) return;
    const ind = taskDataRow.cols.findIndex(i => i.field === field);
    if (ind < 0) return;
    taskDataRow.cols[ind][name] = value;
};

export const getUpdatedTaskDataRow = (taskDataRow: ITaskDataRow, taskDataRowCols: ITaskDataRowCol[]): ITaskDataRow => {
    const result = new TaskDataRow(taskDataRow);
    result.cols = taskDataRowCols;
    return result;
};

//Form Designer Component Properties Width
const getGenericWidthFromClass = (classes: string, hiddenStyle: string, widthStylePrefixes: string[]) => {
    //console.log("getGenericWidthFromClass S", `'${classes}'`, hiddenStyle, widthStylePrefixes);
    if (!classes) return 12;
    const styles = classes
        .split(" ")
        .filter(i => !!i);
    if (!styles.length) return 12;
    if (styles.find(i => i.equalIgnoreCase(hiddenStyle) || i.equalIgnoreCase(TaskTypeClassHidden.Generic))) return 0;
    let style = "";
    for (const widthStylePrefix of widthStylePrefixes) {
        style = styles.find(i => !!i.match(`${widthStylePrefix}\\d+`));
        if (style) break;
    }
    if (!style) return 12;
    const result = style.numberString(false).toInteger();
    //console.log("getGenericWidthFromClass E", result);
    return result;
};

export const getWebWidthFromClass = (classes: string) => getGenericWidthFromClass(classes, TaskTypeClassHidden.Web, [TaskTypeClassWidthPrefix.Web, TaskTypeClassWidthPrefix.Web2]);
        
export const getMobileWidthFromClass = (classes: string) => getGenericWidthFromClass(classes, TaskTypeClassHidden.Mobile, [TaskTypeClassWidthPrefix.Mobile, TaskTypeClassWidthPrefix.Web, TaskTypeClassWidthPrefix.Web2]);

export const getPrintWidthFromClass = (classes: string) => getGenericWidthFromClass(classes, TaskTypeClassHidden.Print, [TaskTypeClassWidthPrefix.Print, TaskTypeClassWidthPrefix.Web, TaskTypeClassWidthPrefix.Web2]);

export const getUpdatedClassForWidthSub = (classes: string, removeSizePrefixes: TaskTypeClassWidthPrefix[], ignoreSizePrefixes: TaskTypeClassWidthPrefix[], sizePrefix: TaskTypeClassWidthPrefix, hiddenStyle: TaskTypeClassHidden, value: number) => {
    const styles = (classes ?? "")
        .split(" ")
        .filter(i => !!i);
    const ignoredStyles = styles
        .filter(i => ignoreSizePrefixes.find(j => i.startsWith(j)));
    return styles
        .filter(i => !ignoreSizePrefixes.find(j => i.startsWith(j)))
        .filter(i => i !== hiddenStyle && !removeSizePrefixes.find(j => i.startsWith(j)))
        .concat(value > 0 ? `${sizePrefix}${value}` : hiddenStyle)
        .concat(ignoredStyles)
        .sort()
        .join(" ");
};

export const getUpdatedClassForWidth = (classes: string, removeSizePrefixes: TaskTypeClassWidthPrefix[], ignoreSizePrefixes: TaskTypeClassWidthPrefix[], sizePrefix: TaskTypeClassWidthPrefix,
    hiddenStyle: TaskTypeClassHidden, value: number, oldWidth: number, previewMode: boolean) => {
    //console.log("getUpdatedClassForWidth S", `'${classes}'`, removeSizePrefixes, ignoreSizePrefixes, sizePrefix, hiddenStyle, value, oldWidth, previewMode);
    let result = getUpdatedClassForWidthSub(classes, removeSizePrefixes, ignoreSizePrefixes, sizePrefix, hiddenStyle, value);
    //Change mobile and print widths of non hidden same width columns when web width is changed
    if (!previewMode && (sizePrefix === TaskTypeClassWidthPrefix.Web || sizePrefix === TaskTypeClassWidthPrefix.Web2)) {
        const mobileWidth = getMobileWidthFromClass(result);
        if (mobileWidth > 0 && mobileWidth === oldWidth) {
            result = getUpdatedClassForWidthSub(result, [], [], TaskTypeClassWidthPrefix.Mobile, TaskTypeClassHidden.Mobile, value);
        }
        const peintWidth = getPrintWidthFromClass(result);
        if (peintWidth > 0 && peintWidth === oldWidth) {
            result = getUpdatedClassForWidthSub(result, [], [], TaskTypeClassWidthPrefix.Print, TaskTypeClassHidden.Print, value);
        }
    }
    //console.log("getUpdatedClassForWidth E", `'${result}'`);
    return result;
};

//Form Designer

/* eslint-disable no-unused-vars */
export const enum FormDesignBuilderDroppableId {
    Picker = "componentPickerDroppable",
    Header = "headerDroppable",
    Form = "formDroppable",
    Row = "rowDroppable"
}
/* eslint-enable no-unused-vars */

export interface FormDesignerDataRow {
    id: string;
    locked?: boolean;
    width?: number;
    rows: ITaskTypeCol[];
}

export const calculateRows = (rows: ITaskTypeCol[], prefix: string) => {
    const result: FormDesignerDataRow[] = [
        {
            id: `${prefix}-row-0`,
            locked: false,
            width: 0,
            rows: [],
        }
    ];

    rows?.forEach((item) => {
        const itemWidth = getWebWidthFromClass(item.class);

        if ((result[result.length - 1].width + itemWidth) > 12) {
            result.push({
                id: `${prefix}-row-${result.length}`,
                locked: false,
                width: 0,
                rows: [],
            });
        }

        result[result.length - 1].rows?.push(item);
        result[result.length - 1].width += itemWidth;
    });
    
    return result;
};

export const calculateStartIndex = (formCols: ITaskTypeCol[], droppableId: string) => formCols.findIndex(item => item.field === droppableId);

export const calculateEndIndex = (dataRows: FormDesignerDataRow[], source: string, destination: string, index: number, startIndex: number) => {
    let result = 0;
    if (destination === FormDesignBuilderDroppableId.Header || destination === FormDesignBuilderDroppableId.Form) {
        result = dataRows.reduce((accumulator, item, currIndex) => accumulator + (currIndex < index ? item.rows.length : 0), 0);
        if (startIndex >= 0 && startIndex < result) {
            result -= 1;
        }
    } else {
        const rowId = dataRows.findIndex(item => item.id === destination);
        result = dataRows.reduce((accumulator, item, currIndex) => accumulator + (currIndex < rowId ? item.rows.length : 0), 0) + index;
        if (startIndex >= 0 && source !== destination && startIndex < result) {
            result -= 1;
        }
    }
    return result;
};

export const getExtraParametersTaskType = (): ITaskEditTypeItem => {
    const result = new TaskEditTypeItem();
    const formCol = new TaskTypeCol();
    formCol.field = "GroupingType";
    formCol.title = Translations.ResultGrouping;
    formCol.dataType = TaskDataRowDataType.String;
    formCol.idOptions = [
        IdTitle.createIdTitle("0", Translations.GroupByMonth),
        IdTitle.createIdTitle("1", Translations.GroupByQuarter),
        IdTitle.createIdTitle("2", Translations.GroupByHalfYear),
        IdTitle.createIdTitle("3", Translations.GroupByYear)
    ];
    result.formCols.push(formCol);
    return result;
};

export const getExtraParametersFromReportFilter = (taskType: TaskEditTypeItem, extraParameters: ITaskDataRow, reportFilter: any) => {
    if (!reportFilter || !taskType || !extraParameters) return;
    const result = new TaskDataRow(extraParameters);
    for (const col of result.cols) {
        const typeCol = taskType.formCols.find(i => i.field === col.field);
        if (!typeCol || !Object.prototype.hasOwnProperty.call(reportFilter, col.field)) continue;
        col.setTaskDataRowColValueFromDbValue(typeCol, reportFilter[col.field]);
    }
    if (taskType) {
        result.initializeCols(taskType);
    }
    return result;
};